/**
 * Motion primitives.
 *
 * One place for every duration and easing, so the whole product moves as a
 * single system rather than each screen inventing its own timing.
 *
 * Rules that constrain everything here:
 *   - transform and opacity ONLY, never layout properties, so this holds
 *     60fps on a mid-range 10" tablet
 *   - nothing blocks input; a technician's Start tap is never gated behind
 *     a transition
 *   - `prefers-reduced-motion` collapses all of it (handled in styles.css and
 *     by MotionConfig in main.tsx)
 */

import type { Transition, Variants } from "framer-motion";

export const DURATION = {
  instant: 0.1,
  fast: 0.15,
  base: 0.2,
  slow: 0.32,
} as const;

/** Standard ease-out. Things arrive quickly and settle. */
export const EASE = [0.22, 1, 0.36, 1] as const;

export const spring: Transition = {
  type: "spring",
  stiffness: 420,
  damping: 34,
  mass: 0.7,
};

/** Snappier spring for drag — cards should feel light under a finger. */
export const dragSpring: Transition = {
  type: "spring",
  stiffness: 600,
  damping: 40,
  mass: 0.5,
};

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE } },
  exit: { opacity: 0, y: -4, transition: { duration: DURATION.fast, ease: EASE } },
};

export const fade: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: DURATION.base } },
  exit: { opacity: 0, transition: { duration: DURATION.fast } },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: spring },
  exit: { opacity: 0, scale: 0.98, transition: { duration: DURATION.fast } },
};

/** Lists stagger in at ~30ms so a loaded view reads as arriving, not blinking. */
export const staggerList: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.03, delayChildren: 0.02 } },
};

export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE } },
};

/** Route transitions: short cross-fade with a slight lift. */
export const pageTransition: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE } },
  exit: { opacity: 0, y: -4, transition: { duration: DURATION.fast, ease: EASE } },
};

/** A brief pulse so a status change is noticeable from across the workshop. */
export const pulse: Variants = {
  idle: { scale: 1 },
  pulse: {
    scale: [1, 1.04, 1],
    transition: { duration: 0.45, ease: EASE },
  },
};
